/**
 * ModelSelector — dropdown de seleção de modelo LLM
 * Usado no ChatHubPage (empty state + input bar ativo)
 * Usa tokens do design system
 */
import { T } from '../../styles/tokens';
import { Signal } from '@preact/signals';

interface LLMModel {
  id: string;
  name: string;
  provider: string;
}

interface ModelSelectorProps {
  /** Signal com lista de modelos disponíveis */
  models: Signal<LLMModel[]>;
  /** Signal com ID do modelo selecionado */
  selectedModel: Signal<string>;
  /** Signal que controla abertura/fechamento do dropdown */
  dropdownOpen: Signal<boolean>;
}

/** Ícone do provider baseado no nome */
function ProviderIcon({ provider }: { provider: string }) {
  if (provider === 'OpenAI') {
    return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M6 12h12"/></svg>;
  }
  if (provider === 'Anthropic') {
    return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 2L2 19h20L12 2z"/></svg>;
  }
  if (provider === 'Google') {
    return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>;
  }
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>;
}

export function ModelSelector({ models, selectedModel, dropdownOpen }: ModelSelectorProps) {
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => { dropdownOpen.value = !dropdownOpen.value; }}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '4px 10px',
          fontSize: 13, fontWeight: 600, color: T.textSecondary,
          display: 'flex', alignItems: 'center', gap: 4, borderRadius: 8,
          transition: 'background 0.15s',
        }}
        onMouseEnter={(e: any) => { e.currentTarget.style.background = T.border; e.currentTarget.style.color = T.text; }}
        onMouseLeave={(e: any) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.textSecondary; }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10"/><path d="M2 12h20"/>
          <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
        </svg>
        {models.value.find(m => m.id === selectedModel.value)?.name || 'Auto'}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {dropdownOpen.value && (
        <div style={{
          position: 'absolute', bottom: 'calc(100% + 8px)', left: 0,
          minWidth: 280, background: '#1e1e1e', border: '1px solid #333',
          borderRadius: 16, boxShadow: '0 12px 40px rgba(0,0,0,0.7)', zIndex: 200,
          maxHeight: 400, overflowY: 'auto', overflow: 'hidden', animation: 'scaleIn 0.15s ease-out',
        }}>
          {models.value.map((m, idx) => {
            const isSelected = selectedModel.value === m.id;
            const prevModel = idx > 0 ? models.value[idx - 1] : null;
            const showSeparator = prevModel && prevModel.provider !== m.provider;
            return (
              <div key={m.id}>
                {showSeparator && <div style={{ height: 1, background: T.bgHover, margin: '4px 0' }} />}
                <button
                  onClick={() => { selectedModel.value = m.id; dropdownOpen.value = false; }}
                  style={{
                    display: 'flex', alignItems: 'center', width: '100%', padding: '12px 16px', border: 'none',
                    background: isSelected ? '#2a2a2a' : '#1e1e1e',
                    color: T.text, cursor: 'pointer', textAlign: 'left', gap: 12, transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e: any) => { e.currentTarget.style.background = '#2a2a2a'; }}
                  onMouseLeave={(e: any) => { e.currentTarget.style.background = isSelected ? '#2a2a2a' : '#1e1e1e'; }}
                >
                  <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: T.border, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textSecondary }}>
                    <ProviderIcon provider={m.provider} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{m.name}</div>
                    <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2 }}>{m.provider}</div>
                  </div>
                  {isSelected && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.text} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="20 6 9 17 4 12"/></svg>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
